import { Empty } from "antd";
import React, { useEffect, useRef } from "react";

import * as Avatar from "~/Avatar";
import * as Color from "~/Color";
import * as Divider from "~/Divider";
import * as Layout from "~/Layout";
import * as Loading from "~/Loading";
import * as Messaging from "~/Messaging";
import { Time } from "~/Time";
import * as Typography from "~/Typography";

import * as Send from "./Send";

export * as Action from "./Action";
export * as Active from "./Active";
export * as Send from "./Send";

type MessageGrouping = {
  readonly date: string;
  readonly messages: readonly Messaging.Message[];
};

const groupMessagesByDay = (
  messages: readonly Messaging.Message[]
): readonly MessageGrouping[] =>
  messages
    .map((message) => ({
      date: Time.DateTime.toDateOnlyMMMDYYYY(message.date),
      messages: [message],
    }))
    .reduce((previous: readonly MessageGrouping[], current) => {
      const addNewGroup = !previous.some(
        (grouping) => grouping.date === current.date
      );
      const newPrevious = previous.map((grouping) => {
        if (grouping.date === current.date) {
          return {
            date: grouping.date,
            messages: [...grouping.messages, ...current.messages],
          };
        }
        return grouping;
      });
      if (addNewGroup) {
        return [...previous, current];
      }
      return newPrevious;
    }, []);

export const Component: React.Fn = () => {
  return Messaging.MessageInteraction.Active.useWhen({
    inactive: () => (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        style={{ padding: "2em" }}
        description={<span>Select a driver to enter into a conversation</span>}
      />
    ),
    unavailable: () => <Loading.Component />,
    available: (messages) => <Interaction.Component messages={messages} />,
  });
};

export namespace Interaction {
  type Props = {
    readonly messages: readonly Messaging.Message[];
  };
  export const Component: React.Fn<Props> = ({ messages }) => {
    const enterMessageRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
      // eslint-disable-next-line functional/no-expression-statement
      enterMessageRef &&
        enterMessageRef.current &&
        enterMessageRef.current.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(scrollToBottom, [messages]);

    const groupedMessages = groupMessagesByDay(messages);

    return (
      <>
        <div style={{ height: "70vh", overflowX: "auto" }}>
          {groupedMessages.map((dayGroup) => (
            <MessageDay.Component key={dayGroup.date} day={dayGroup} />
          ))}
          <span ref={enterMessageRef}>&nbsp;</span>
        </div>
        <Send.Component />
      </>
    );
  };
}

export namespace MessageDay {
  type Props = {
    readonly day: MessageGrouping;
  };
  export const Component: React.Fn<Props> = ({ day }) => {
    const messageDate =
      Time.DateTime.toDateOnlyMMMDYYYY(Time.DateTime.now()) === day.date
        ? "Today"
        : day.date;
    return (
      <div>
        <Divider.Component titleOrientation="left">
          {messageDate}
        </Divider.Component>
        {day.messages.map((message) => (
          <MessageExchange.Component
            key={message.date.dateTime}
            message={message}
          />
        ))}
      </div>
    );
  };

  export namespace MessageExchange {
    type Props = {
      readonly message: Messaging.Message;
    };
    export const Component: React.Fn<Props> = ({ message }) =>
      message.sender === "DRIVER" ? (
        <DriverMessage.Component message={message} />
      ) : (
        <DispatchMessage.Component message={message} />
      );
  }

  export namespace DriverMessage {
    type Props = {
      readonly message: Messaging.Message;
    };
    export const Component: React.Fn<Props> = ({ message }) => {
      const initials = Messaging.MessageInteraction.Active.useActiveInitials();
      return (
        <Layout.Row.Component
          align="middle"
          justify="start"
          style={{
            width: "50%",
            paddingBottom: "1em",
          }}
        >
          <Layout.Column.Component
            span={2}
            style={{
              maxWidth: "3em",
              paddingRight: "0.5em",
            }}
          >
            <Avatar.Component style={{ backgroundColor: Color.steel() }}>
              {initials}
            </Avatar.Component>
          </Layout.Column.Component>
          <Layout.Column.Component
            span={21}
            style={{ alignSelf: "flex-start" }}
          >
            <Bubble.Component
              background={Color.azure()}
              foreground={Color.white()}
              message={message}
            />
          </Layout.Column.Component>
        </Layout.Row.Component>
      );
    };
  }
  export namespace DispatchMessage {
    type Props = {
      readonly message: Messaging.Message;
    };
    export const Component: React.Fn<Props> = ({ message }) => (
      <Layout.Row.Component justify="end" style={{ paddingBottom: "1em" }}>
        <Layout.Column.Component style={{ maxWidth: "50%" }}>
          <Bubble.Component
            background={Color.gray()}
            foreground={Color.white()}
            message={message}
          />
        </Layout.Column.Component>
      </Layout.Row.Component>
    );
  }
}

export namespace Bubble {
  type Props = {
    readonly message: Messaging.Message;
    readonly left?: boolean;
    readonly right?: boolean;
    readonly background?: Color.Color;
    readonly foreground?: Color.Color;
  };
  export const Component: React.Fn<Props> = ({
    message,
    background,
    foreground,
  }) => {
    const zone = Time.Zone.use();
    return (
      <>
        <div>
          <Typography.Component muted small thin align="left">
            {Time.DateTime.toHMMSS(message.date, zone)}
          </Typography.Component>
        </div>
        <div
          className="text-wrap"
          style={{
            backgroundColor: background ? background : Color.white(),
            color: foreground ? foreground : Color.translucentBlack(),
            padding: "0.8em",
            borderRadius: "6px",
            width: "fit-content",
          }}
        >
          {message.text}
        </div>
      </>
    );
  };
}
